import {
  useEffect,
  useId,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
  useCallback,
  useContext,
  KeyboardEvent,
  MouseEvent,
} from "react";
import {
  Placement,
  size,
  flip,
  limitShift,
  offset,
  shift,
  computePosition,
  autoUpdate,
} from "@floating-ui/react";
import cx from "classnames";
import Up from "@servicetitan/hammer-icon/mdi/round/arrow_drop_up.svg";
import Down from "@servicetitan/hammer-icon/mdi/round/arrow_drop_down.svg";

import { Button, ButtonProps } from "../Button";
import { childrenToString } from "../../internal/functions";
import { DialogContext } from "../Dialog/internal/DialogContext";
import { getActiveElement } from "../../internal/functions";
import { Icon, IconProps } from "../Icon";
import { safeHidePopover, safeShowPopover } from "../../internal/functions";
import { useTrackingId } from "../../hooks";
import { DataTrackingId } from "../../types";

import { MenuContext } from "./internal/MenuContext";
import { MenuItem } from "./MenuItem";
import { MenuNested } from "./internal/MenuNested";
import { MenuCommonProps } from "./types";

import styles from "./Menu.module.scss";

/**
 * Props for the Menu component
 * @extends Omit<ButtonProps, "icon">
 * @extends MenuCommonProps
 */
export type MenuProps = Omit<ButtonProps, "icon"> &
  MenuCommonProps & {
    /**
     * Icon to display on the menu button
     */
    icon?: IconProps["svg"];
  } & DataTrackingId;

/**
 * Menu component for displaying dropdown menus with navigation and actions.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Automatic positioning with fallback strategies
 * - Keyboard navigation with arrow keys and escape
 * - Custom trigger elements
 * - Nested menu support
 * - Automatic height adjustment
 * - Scroll indicators for overflow content
 * - Fully accessible with proper ARIA attributes
 * - Outside click detection and handling
 *
 * @example
 * <Menu label="Edit">
 *   <Menu.Item label="Cut" onClick={() => console.log('Cut clicked')} />
 *   <Menu.Item label="Copy" onClick={() => console.log('Copy clicked')} />
 *   <Menu label="Copy as">
 *     <Menu.Item label="Text" onClick={() => console.log('Copy as Text')} />
 *     <Menu.Item label="Video" onClick={() => console.log('Copy as Video')} />
 *     <Menu label="Image">
 *       <Menu.Item label=".png" onClick={() => console.log('Copy as PNG')} />
 *       <Menu.Item label=".jpg" onClick={() => console.log('Copy as JPG')} />
 *     </Menu>
 *   </Menu>
 */
export const Menu = Object.assign(
  function MenuInner(props: MenuProps) {
    const {
      children,
      placement = "bottom-start",
      open,
      defaultOpen = false,
      disableAutoHeight,
      label,
      onOutsidePress,
      trigger,
      contentClassName,
      maxHeight,
      ...rest
    } = props;

    const uid = useId();
    const [openState, setOpenState] = useState<boolean>(open ?? defaultOpen);
    const [scrollTop, setScrollTop] = useState(0);
    const [menuHeight, setMenuHeight] = useState<number | null>(null);
    const [arrowStart, setArrowStart] = useState(false);
    const [arrowEnd, setArrowEnd] = useState(false);
    const invokerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const childrenRef = useRef<HTMLDivElement>(null);
    const [focusItems, setFocusItems] = useState<HTMLElement[]>();
    const dialogContext = useContext(DialogContext);
    const menuContext = useContext(MenuContext);

    // TODO: Find a way to test these properly!
    // Stryker disable all : Currently relying on Chromatic test for all visuals
    const fallbackArr = useCallback(() => {
      if (placement.startsWith("top")) {
        return disableAutoHeight
          ? [placement]
          : ["top-start", "top-end", "bottom-start", "bottom-end"];
      }
      return disableAutoHeight
        ? [placement]
        : ["bottom-start", "bottom-end", "top-start", "top-end"];
    }, [disableAutoHeight, placement]);

    /* istanbul ignore next */
    const updatePosition = useCallback(() => {
      if (!invokerRef.current || !menuRef.current) return;

      computePosition(invokerRef.current, menuRef.current, {
        placement: placement,
        middleware: [
          offset({
            mainAxis: 5,
            alignmentAxis: 0,
          }),
          flip({
            fallbackStrategy: "bestFit",
            fallbackPlacements: fallbackArr() as Placement[],
            padding: 50, // Setting to 50px as it is the size of menu with 1 item in it
          }),
          shift({
            mainAxis: !menuContext?.disableAutoHeight,
            crossAxis: false,
            limiter: limitShift({
              mainAxis: !menuContext?.disableAutoHeight,
            }),
          }),
          size({
            padding: 8,
            apply({ availableWidth, availableHeight, elements }) {
              if (disableAutoHeight ?? menuContext?.disableAutoHeight) return;
              const newHeight = availableHeight - 16;
              if (Math.abs((menuHeight ?? 0) - newHeight) > 1) {
                setMenuHeight(newHeight);
                Object.assign(elements.floating.style, {
                  maxWidth: `${availableWidth - 16}px`,
                  maxHeight: maxHeight
                    ? `${Math.min(newHeight, typeof maxHeight === "number" ? maxHeight : parseInt(maxHeight.toString()))}px`
                    : `${newHeight}px`,
                });
              }
            },
          }),
        ],
      }).then(({ x, y }) => {
        if (!menuRef.current) return;
        Object.assign(menuRef.current.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
        menuRef.current.dataset.positioned = "true";
      });
    }, [
      placement,
      fallbackArr,
      menuContext?.disableAutoHeight,
      disableAutoHeight,
      menuHeight,
      maxHeight,
    ]);

    const menuReference = useRef<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
      if (!openState) {
        setMenuHeight(null);
        setScrollTop(0);
        return;
      }
      if (!menuRef.current) return;
      menuReference.current = menuRef.current;

      let resizeTimeout: number | null = null;
      const lastDimensions = {
        height: menuReference.current.clientHeight,
        scrollHeight: childrenRef.current?.scrollHeight ?? 0,
      };

      const updateArrows = (height: number, scrollHeight: number) => {
        if (!childrenRef.current) return;
        const isOverflow = scrollHeight > height;
        setArrowStart(isOverflow && childrenRef.current.scrollTop > 0);
        setArrowEnd(isOverflow && scrollHeight - height - scrollTop - 16 > 0);
      };

      const observer = new ResizeObserver(() => {
        if (!childrenRef.current || !menuReference.current) return;

        if (resizeTimeout) {
          window.clearTimeout(resizeTimeout);
        }

        resizeTimeout = window.setTimeout(() => {
          const newHeight = menuReference.current?.clientHeight ?? 0;
          const newScrollHeight = childrenRef.current?.scrollHeight ?? 0;

          if (!newHeight) return;

          lastDimensions.height = newHeight;
          lastDimensions.scrollHeight = newScrollHeight;

          updateArrows(newHeight, newScrollHeight);
        }, 150);
      });

      if (menuReference.current) {
        observer.observe(menuReference.current);
      }

      return () => {
        if (resizeTimeout) {
          window.clearTimeout(resizeTimeout);
        }
        if (menuReference.current) {
          observer.unobserve(menuReference.current);
        }
        observer.disconnect();
      };
    }, [openState, scrollTop]);

    // Effect to add autoUpdate to eventListener
    // while the popover is opened
    useLayoutEffect(() => {
      if (!menuRef.current || !invokerRef.current) return;
      if (!openState) return;

      let requestID: number | null = null;
      const cleanup = autoUpdate(invokerRef.current, menuRef.current, () => {
        requestID = requestAnimationFrame(updatePosition);
      });

      return () => {
        if (requestID) cancelAnimationFrame(requestID);
        cleanup();
      };
    }, [updatePosition, openState]);
    // Stryker restore all

    // Main function to open menu
    // This gets canceled if close is called before the promise is resolved
    // Ex) Allows cursor jump from invoker to menu
    //     without closing
    const openMenu = useCallback(async () => {
      if (!menuRef.current || !childrenRef.current) return;

      safeShowPopover(menuRef.current);

      // Position the menu before showing it
      if (!invokerRef.current || !menuRef.current) return;

      await computePosition(invokerRef.current, menuRef.current, {
        placement: placement,
        middleware: [
          offset({
            mainAxis: 5,
            alignmentAxis: 0,
          }),
          flip({
            fallbackStrategy: "bestFit",
            fallbackPlacements: fallbackArr() as Placement[],
            padding: 50, // Setting to 50px as it is the size of menu with 1 item in it
          }),
          shift({
            mainAxis: !menuContext?.disableAutoHeight,
            crossAxis: false,
            limiter: limitShift({
              mainAxis: !menuContext?.disableAutoHeight,
            }),
          }),
        ],
      }).then(({ x, y }) => {
        if (!menuRef.current) return;
        Object.assign(menuRef.current.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
        menuRef.current.dataset.positioned = "true";
      });

      setOpenState(true);

      // Wait for next frame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (!childrenRef.current) return;
        const focusables = Array.from(
          childrenRef.current.querySelectorAll(
            `#scroller-${uid.replace(/:/g, "")} > button, #scroller-${uid.replace(/:/g, "")} > div[data-nested="true"] > button, #scroller-${uid.replace(/:/g, "")} > a[href]`,
          ),
        ) as HTMLElement[];
        setFocusItems(focusables);
      });

      dialogContext?.setHasOpenPopover?.(true);
    }, [
      dialogContext,
      uid,
      placement,
      fallbackArr,
      menuContext?.disableAutoHeight,
    ]);

    // Main function to close menu
    // This gets canceled if close is called before the promise is resolved
    // Ex) Allows popover to be kept closed when cursor
    //     moves out of the invoker and menu
    const closeMenu = useCallback(
      async (fn?: () => void, options?: { skipInvokerRefocus?: boolean }) => {
        if (!menuRef.current || !openState) return;
        fn?.();
        setOpenState(false);
        safeHidePopover(menuRef.current);
        dialogContext?.setHasOpenPopover?.(false);
        if (!options?.skipInvokerRefocus) {
          invokerRef.current?.focus();
        }
      },
      [openState, dialogContext],
    );

    const isControlled = typeof open !== "undefined";

    // Updating openness for controlled
    const initialRender = useRef(true);
    useEffect(() => {
      if (!isControlled) return;
      if (initialRender.current === true) {
        if (open && menuRef.current) {
          openMenu();
        }
        initialRender.current = false;
        return;
      }
      if (open) {
        openMenu();
      } else {
        closeMenu(undefined, { skipInvokerRefocus: true });
      }
      // open ? openMenu() : closeMenu();
    }, [closeMenu, isControlled, open, openMenu, dialogContext]);

    // Effect to attach eventListener for reading click state from
    // globalThis.MouseEvent for composedPath to detect if target is
    // part of the composedPath, then to attempt to close
    useEffect(() => {
      if (typeof document === "undefined") return;

      let isClosable = false;
      const onMouseDownHandler = (e: globalThis.MouseEvent) => {
        if (!invokerRef.current || !menuRef.current) return;
        const isInvoker = e.composedPath().includes(invokerRef.current);
        const isPopover = e.composedPath().includes(menuRef.current);
        if (!isInvoker && !isPopover && openState) {
          onOutsidePress?.(e);
          isClosable = true;
        }
      };
      const onMouseUpHandler = (e: globalThis.MouseEvent) => {
        if (!invokerRef.current || !menuRef.current) return;
        const isInvoker = e.composedPath().includes(invokerRef.current);
        const isPopover = e.composedPath().includes(menuRef.current);
        if (!isInvoker && !isPopover && openState) {
          if (isControlled || !isClosable || !openState) return;

          closeMenu(undefined, { skipInvokerRefocus: true });
        }
      };

      document.addEventListener("mousedown", onMouseDownHandler);
      document.addEventListener("mouseup", onMouseUpHandler);
      return () => {
        document.removeEventListener("mousedown", onMouseDownHandler);
        document.removeEventListener("mouseup", onMouseUpHandler);
      };
    }, [isControlled, openState, onOutsidePress, closeMenu]);

    const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
      if (!openState || !childrenRef.current || menuContext) return;
      if (e.code === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        closeMenu();
      }

      const currentActiveIndex = focusItems?.findIndex(
        (item) => item.id === getActiveElement(document)?.id,
      );

      const firstItem = focusItems?.[0] as HTMLElement;
      const lastItem = focusItems?.[focusItems.length - 1] as HTMLElement;

      if (e.code === "ArrowDown") {
        e.stopPropagation();
        e.preventDefault();
        if (currentActiveIndex === (focusItems?.length ?? 0) - 1) {
          firstItem.focus();
          return;
        }
        const nextItem = focusItems?.[
          (currentActiveIndex ?? 0) + 1
        ] as HTMLElement;
        nextItem.focus();
        return;
      }
      if (e.code === "ArrowUp") {
        e.stopPropagation();
        e.preventDefault();
        if (currentActiveIndex === 0) {
          lastItem.focus();
          return;
        }
        const prevItem = focusItems?.[
          (currentActiveIndex ?? 0) - 1
        ] as HTMLElement;
        prevItem.focus();
        return;
      }
    };

    const value = useMemo(() => {
      return {
        disableAutoHeight,
        closeRootMenu: menuContext?.closeRootMenu ?? closeMenu,
      };
    }, [disableAutoHeight, menuContext, closeMenu]);

    const MenuButtonOnKeyDownHandler = (
      e: KeyboardEvent<HTMLButtonElement>,
    ) => {
      if (e.code === "ArrowDown") {
        focusItems?.[0].focus();
        e.stopPropagation();
        e.preventDefault();
      }
    };
    const MenuButtonOnClick = () => {
      if (isControlled) return;
      if (openState) {
        closeMenu();
        return;
      }
      openMenu();
    };

    const data = {
      label: childrenToString(props.label),
    };

    const trackingId = useTrackingId({
      name: "Menu",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    if (menuContext) {
      return (
        <MenuContext.Provider value={value}>
          <MenuNested
            data-tracking-id={trackingId}
            open={open}
            defaultOpen={defaultOpen}
            className={props.className}
            icon={props.icon}
            label={label}
          >
            {children}
          </MenuNested>
        </MenuContext.Provider>
      );
    }
    return (
      <MenuContext.Provider value={value}>
        {trigger ? (
          trigger({
            ref: invokerRef,
            onClick: () => MenuButtonOnClick(),
            onKeyDown: MenuButtonOnKeyDownHandler,
            "aria-haspopup": true,
            "aria-controls": `menu-${uid.replace(/:/g, "")}`,
            "aria-expanded": openState,
            "data-state": openState ? "open" : "close",
          })
        ) : (
          <Button
            ref={invokerRef}
            onClick={MenuButtonOnClick}
            onKeyDown={MenuButtonOnKeyDownHandler}
            aria-haspopup={true}
            aria-controls={`menu-${uid.replace(/:/g, "")}`}
            data-state={openState ? "open" : "close"}
            aria-expanded={openState}
            {...rest}
          >
            {label}
          </Button>
        )}
        <div
          popover="manual"
          id={`menu-${uid.replace(/:/g, "")}`}
          data-anv="menu-content"
          className={cx(styles.content, contentClassName)}
          onKeyDown={onKeyDownHandler}
          ref={menuRef}
          role="presentation"
        >
          {arrowStart && (
            <div className={styles["arrow-up"]} aria-hidden>
              <Icon svg={Up} size="xlarge" className={styles["arrow-icon"]} />
            </div>
          )}
          <div
            className={styles.scroller}
            tabIndex={-1}
            ref={childrenRef}
            style={{
              position: "relative",
              maxHeight: maxHeight
                ? `${Math.min(menuHeight ?? Infinity, typeof maxHeight === "number" ? maxHeight : parseInt(maxHeight.toString()))}px`
                : (menuHeight ?? "inherit"),
            }}
            onScroll={(e: MouseEvent<HTMLDivElement>) =>
              setScrollTop(e.currentTarget.scrollTop)
            }
            role="menu"
            id={`scroller-${uid.replace(/:/g, "")}`}
          >
            {children}
          </div>
          {arrowEnd && (
            <div className={styles["arrow-down"]} aria-hidden>
              <Icon svg={Down} size="xlarge" className={styles["arrow-icon"]} />
            </div>
          )}
        </div>
      </MenuContext.Provider>
    );
  },
  {
    /**
     * MenuItem component for individual menu options.
     *
     * Features:
     * - Supports icons and labels
     * - Keyboard navigation support
     * - Disabled state handling
     * - Automatic menu closing on selection
     * - Fully accessible with proper ARIA attributes
     * - Focus management and tab index handling
     * - Click and mouse enter event handling
     *
     * @example
     * <Menu.Item
     *   label="Edit Profile"
     *   icon={EditIcon}
     *   onClick={() => console.log('Edit profile clicked')}
     * />
     */
    Item: MenuItem,
  },
);
